import { createShapeElement, MASK_PRESETS, type MaskPreset } from "@cva/shared";
import { useBlockActions } from "./BlocksPanel";
import { LibraryBlockTile } from "./block-tiles";
import { addElements } from "./commands";
import { MaskIcon } from "./MaskPicker";
import { activePageDoc, type EditorStore, useEditorApi } from "./store";

/** Frames that are an image placeholder in a form (the most common ones first). */
const FRAME_MASKS = [
  "circle",
  "arch",
  "heart",
  "blob1",
  "speech",
  "hexagon",
  "star",
  "drop",
  "leaf",
  "blob2",
  "blob3",
  "diamond",
  "triangle",
  "burst",
  "wave",
  "ellipse",
]
  .map((id) => MASK_PRESETS.find((preset) => preset.id === id))
  .filter((preset): preset is MaskPreset => preset !== undefined);

/** Image placeholder in the form of a mask, centred on the page. */
function addFrame(store: EditorStore, preset: MaskPreset) {
  const doc = activePageDoc(store);
  const size = Math.round(Math.min(doc.format.width, doc.format.height) * 0.45);
  const element = createShapeElement("rect", {
    x: Math.round((doc.format.width - size) / 2),
    y: Math.round((doc.format.height - size) / 2),
    width: size,
    height: size,
    name: `Rahmen ${preset.label}`,
    fill: "#e4e4e7",
    placeholder: "image",
    mask: { ...preset.mask },
  });
  addElements(store, [element]);
}

/**
 * "Rahmen" in the Elemente panel: image placeholders in a form and the
 * decorative photo frames (Bausteine) – an image dragged onto one fills it.
 */
export function FramesInsert() {
  const store = useEditorApi();
  const { blocks, buildOptions, insertBlock } = useBlockActions();
  const decorative = blocks.filter((block) => block.id.startsWith("bilderrahmen-"));
  return (
    <div className="space-y-2">
      <div className="grid grid-cols-6 gap-1.5">
        {FRAME_MASKS.map((preset) => (
          <button
            key={preset.id}
            type="button"
            title={`Rahmen: ${preset.label}`}
            aria-label={`Rahmen: ${preset.label}`}
            onClick={() => addFrame(store, preset)}
            className="flex aspect-square items-center justify-center rounded-lg border border-zinc-200 bg-zinc-50 text-zinc-500 transition hover:border-brand-300 hover:bg-brand-50 hover:text-brand-700"
          >
            <MaskIcon mask={preset.mask} size={26} />
          </button>
        ))}
      </div>
      {decorative.length > 0 ? (
        <div className="grid grid-cols-2 gap-2">
          {decorative.map((block) => (
            <LibraryBlockTile
              key={block.id}
              block={block}
              buildOptions={buildOptions}
              onInsert={() => insertBlock(block)}
              width={124}
              height={96}
            />
          ))}
        </div>
      ) : null}
      <p className="text-[11px] text-zinc-500">
        Ein Bild aus den Medien auf den Rahmen ziehen – es füllt die Form.
      </p>
    </div>
  );
}
